/**
 * WhatsApp Integration API Service (P204).
 *
 * WHY THIS IS NOT AN OAUTH MODULE. Every other card in the registry sends the person to a
 * provider's consent screen (`getConnectUrl`). WhatsApp has no consent screen: the person links
 * THEIR OWN WhatsApp to our gateway by scanning a code — the same gesture as linking WhatsApp Web.
 * So the connect step happens IN PLACE, on the card, and this module is what that panel talks to.
 *
 * WHY THE PANEL TALKS TO raisy_be AND NOT TO THE GATEWAY. The gateway is a separate service that
 * holds the WhatsApp session and takes a service token on every call. Fetching it directly from the
 * browser would mean shipping that token to the browser, so the BE is the only party that reaches
 * the gateway and this module only ever reaches the BE (P204 D-204-004).
 */

import AuthUtils from "@/providers/firebase/authUtils";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

export interface ApiResponse<T = Record<string, unknown>> {
  status: boolean;
  statusCode: number;
  message: string;
  data: T | null;
}

async function apiCall<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const headers = await AuthUtils.getAuthHeaders();
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { ...headers, ...options.headers },
  });
  return res.json();
}

export interface WhatsAppStatusResponse {
  /** True only when the session is USABLE — linked AND the socket is open. */
  connected: boolean;
  /**
   * The gateway's raw socket state. This exists because `connected: true` with
   * `connection: "closed"` is a real state — WhatsApp logs the device out and the gateway keeps
   * the association — and the two need different instructions: "link your number" and "your
   * number was logged out".
   */
  connection: string | null;
  /** The linked phone, once known. Never fabricated: null until the gateway reports it. */
  phone: string | null;
  connected_at: string | null;
  /**
   * The gateway's ban-risk level (`low` | `medium` | `high` | `critical`). Reported rather than
   * only logged, because a restriction is the one failure of this transport the person cannot see
   * for themselves until WhatsApp tells them (P204 C6).
   */
  risk: string | null;
  /** WHY the risk is what it is, in the gateway's own words — the actionable half of the number. */
  risk_reasons: string[];
  risk_recommendation: string | null;
}

export interface WhatsAppQrResponse {
  connected: boolean;
  /** The raw code, for a client that renders its own. */
  qr: string | null;
  /** A PNG data URL, so this surface needs no QR library of its own. */
  qr_png: string | null;
  age_seconds: number | null;
  /**
   * True once the code is old enough that WhatsApp has probably replaced it. The surface shows
   * "expired, fetching a fresh one" rather than leaving a dead code on screen looking scannable.
   */
  may_be_stale: boolean;
  /** Machine-readable reason when there is no code yet (`already_linked`, `not_issued_yet`, …). */
  reason: string | null;
  message: string | null;
}

export function getWhatsAppStatus(
  entityId: string,
): Promise<ApiResponse<WhatsAppStatusResponse>> {
  return apiCall(
    `/api/v1/integrations/whatsapp/status?entity_id=${encodeURIComponent(entityId)}`,
  );
}

export function getWhatsAppQr(entityId: string): Promise<ApiResponse<WhatsAppQrResponse>> {
  return apiCall(`/api/v1/integrations/whatsapp/qr?entity_id=${encodeURIComponent(entityId)}`);
}

export function getWhatsAppQrPngUrl(entityId: string): string {
  return `${API_URL}/api/v1/integrations/whatsapp/qr.png?entity_id=${encodeURIComponent(entityId)}`;
}

export function disconnectWhatsApp(
  entityId: string,
): Promise<ApiResponse<{ status: string }>> {
  return apiCall(
    `/api/v1/integrations/whatsapp/disconnect?entity_id=${encodeURIComponent(entityId)}`,
    { method: "POST" },
  );
}

/* ── THE MESSAGING BOARD (P204 step 6) ────────────────────────────────────────────────────────────
 *
 * The read-and-decide half of the channel, on the SAME module as the link half on purpose: the board
 * and the connect card must never disagree about what "connected" means, so they read the same
 * `/status`.
 *
 * WHAT THE BOARD IS: a list of contacts, the open thread, and — under each inbound message — the
 * reply the machine prepared for it, which the person accepts, corrects or rejects.
 *
 * WHY THERE IS NO FREE-TEXT SEND BOX. This channel has exactly ONE send path and it runs through a
 * prepared reply (`/replies/{id}/send`). A box at the top of the thread that could send anything
 * would be a second, ungated way to speak as the client — and the whole reason this channel has a
 * human gate is that the gate is the product (D-204-007). So the only editable text on this page is
 * the draft that already exists.
 *
 * `entity_id` IS THE CHANNEL OWNER — the client whose WhatsApp is linked — NOT the prospect. The
 * prospect is the thread. The BE enforces this (`_require_read`); the surface must pass the entity
 * the operator is working as and never anything derived from a conversation.
 */

export interface WhatsAppConversationSummary {
  /** The transport's thread id (`<phone>@s.whatsapp.net`) — an opaque handle, never shown raw. */
  thread_id: string;
  /** The contact's number, as WhatsApp gives it — the label a human recognises. */
  phone: string;
  /** The entity the PROSPECT belongs to, which may differ from the channel owner. */
  entity_id: string | null;
  last_message: string | null;
  last_direction: string | null;
  last_at: string | null;
  /** Prepared replies still waiting on a human — the number that makes the list worth looking at. */
  pending_replies: number;
}

export interface WhatsAppConversationList {
  conversations: WhatsAppConversationSummary[];
  count: number;
}

/**
 * The reply the machine prepared, carried ON its parent message rather than as a second message:
 * one exchange is one thing, and rendering it twice would invent a conversation that did not happen.
 */
export interface WhatsAppPreparedReply {
  communication_id: string;
  body: string;
  /** `pending` | `sent` | `failed` | `cancelled`. */
  status: string;
  /**
   * True when the machine itself says this should not go out unanswered — an unavailable price, a
   * question it could not ground. Shown, because it is the reason the row is worth a human's minute.
   */
  needs_human: boolean;
  /** Why the machine flagged it, in its own words. */
  reason: string | null;
  /** Present only on `failed` — a send the gateway refused. Never a silent drop. */
  send_error: string | null;
}

export interface WhatsAppThreadMessage {
  communication_id: string;
  /** `inbound` (the contact wrote) | `outbound` (we did). */
  direction: string;
  body: string | null;
  at: string | null;
  status: string | null;
  /** The kind of media this message was (`audio`, `image`, …) or null for plain text. */
  media: string | null;
  prepared_reply: WhatsAppPreparedReply | null;
}

export interface WhatsAppThread {
  thread_id: string;
  messages: WhatsAppThreadMessage[];
  count: number;
}

export interface WhatsAppReplyDecision {
  communication_id: string;
  status: string;
  needs_human?: boolean;
}

/** The reason carried by a refusal, for the surface to map onto a state instead of a shrug. */
export function whatsappReasonOf(res: ApiResponse<unknown>): string | null {
  const reason = (res.data as { reason?: string } | null)?.reason;
  return typeof reason === "string" ? reason : null;
}

export function listWhatsAppConversations(
  entityId: string,
  limit = 50,
): Promise<ApiResponse<WhatsAppConversationList>> {
  return apiCall(
    `/api/v1/integrations/whatsapp/conversations?entity_id=${encodeURIComponent(entityId)}&limit=${limit}`,
  );
}

export function getWhatsAppThread(
  entityId: string,
  threadId: string,
): Promise<ApiResponse<WhatsAppThread>> {
  // `thread_id` carries an `@`, and it is a path segment on the BE (`/conversations/{thread_id:path}`),
  // so it is encoded rather than interpolated: a raw `@` survives by luck on some stacks and is
  // consumed as userinfo on others.
  return apiCall(
    `/api/v1/integrations/whatsapp/conversations/${encodeURIComponent(threadId)}` +
      `?entity_id=${encodeURIComponent(entityId)}`,
  );
}

/**
 * Accept a prepared reply and send it. `text` is the operator's own wording and OVERRIDES the draft
 * — correcting a sentence at the moment of approval is the point of having a human here, and making
 * them reject-and-regenerate to change one word would train them to stop reading.
 */
export function sendWhatsAppReply(
  entityId: string,
  communicationId: string,
  text?: string,
): Promise<ApiResponse<WhatsAppReplyDecision>> {
  return apiCall(
    `/api/v1/integrations/whatsapp/replies/${encodeURIComponent(communicationId)}/send` +
      `?entity_id=${encodeURIComponent(entityId)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(text ? { text } : {}),
    },
  );
}

/** Reject a prepared reply. The row is kept as `cancelled` — the refusal is the training signal. */
export function rejectWhatsAppReply(
  entityId: string,
  communicationId: string,
  reason?: string,
): Promise<ApiResponse<WhatsAppReplyDecision>> {
  return apiCall(
    `/api/v1/integrations/whatsapp/replies/${encodeURIComponent(communicationId)}/reject` +
      `?entity_id=${encodeURIComponent(entityId)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(reason ? { reason } : {}),
    },
  );
}
